import { inject, Injectable, InjectionToken } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import type { AppPlugin } from '@capacitor/app';
import { filter, firstValueFrom } from 'rxjs';
import { codeFromTagLink } from '../utils/tag-link';
import { IS_NATIVE_SHELL } from './live-update.service';
import { LoggerService } from './logger.service';
import { SessionStorageService } from './session-storage.service';

/** Loads the App plugin lazily (native shell only), wrapped — a plugin proxy must never be a promise's value. */
export const NATIVE_APP_PLUGIN = new InjectionToken<() => Promise<{ App: AppPlugin }>>('NATIVE_APP_PLUGIN', {
  providedIn: 'root',
  factory: () => async () => {
    const { App } = await import('@capacitor/app');
    return { App };
  },
});

/** The launch URL already handled — getLaunchUrl() returns it again after a web reload (OTA). */
const LAUNCH_HANDLED_KEY = 'tagLink.launchHandled';
/** Android can deliver the launching tag both as the launch URL and as an appUrlOpen event. */
const DUPLICATE_WINDOW_MS = 3000;

/**
 * Opens NFC tag links (https://<host>/t/:code) that reach the native app from outside
 * the scan page: a tag tapped with the app closed, in the background, or open on
 * another page. Android delivers them as verified App Links (autoVerify intent filter +
 * /.well-known/assetlinks.json); iOS through universal links once the
 * Associated Domains setup is done (docs/features/nfc-tags.md).
 *
 * Navigates to /t/:code/open, which waits for auth and sync, then resolves the code.
 */
@Injectable({ providedIn: 'root' })
export class NativeTagLinkService {
  private readonly log = inject(LoggerService).getLogger('tag-link');
  private readonly isNative = inject(IS_NATIVE_SHELL);
  private readonly loadPlugin = inject(NATIVE_APP_PLUGIN);
  private readonly router = inject(Router);
  private readonly session = inject(SessionStorageService);
  private started = false;
  private last: { url: string; at: number } | null = null;

  /** Call at app start, so a tag that cold-starts the app is caught too. */
  async init(): Promise<void> {
    if (!this.isNative || this.started) return;
    this.started = true;
    try {
      const { App } = await this.loadPlugin();
      await App.addListener('appUrlOpen', ({ url }) => void this.open(url));
      const launch = await App.getLaunchUrl();
      if (launch?.url && codeFromTagLink(launch.url) && this.session.getItem(LAUNCH_HANDLED_KEY) !== launch.url) {
        this.session.setItem(LAUNCH_HANDLED_KEY, launch.url);
        await this.open(launch.url);
      }
    } catch (error) {
      this.log.error('Tag link handling init failed:', error);
    }
  }

  private async open(url: string): Promise<void> {
    const code = codeFromTagLink(url);
    if (!code) return;
    const now = Date.now();
    if (this.last?.url === url && now - this.last.at < DUPLICATE_WINDOW_MS) return;
    this.last = { url, at: now };

    // On a cold start, let the initial navigation finish first so it doesn't override this one
    if (!this.router.navigated) {
      await firstValueFrom(this.router.events.pipe(filter((e) => e instanceof NavigationEnd)));
    }
    this.log.info('Opening tag link:', code);
    await this.router.navigate(['/t', code, 'open']);
  }
}
